import React, { useState, useEffect } from 'react';
import {
  CollaboratorAuthService,
  type CollaboratorPortalAccess,
  type CollaboratorPortalPermissions,
  type CollaboratorPortalProject
} from '../../../lib/services/collaboratorAuthService';
import { useProjectStore } from '../../../lib/stores/projectStore';
import { MinimalLoader } from '../../ui/MinimalLoader';
import { Check, X } from 'lucide-react';

// Spostamento puro da CollaboratorPortalAccessManager.tsx (righe ~483-629 dell'originale,
// prima dello split di questo file da collaboratorAccessForms.tsx): stesso markup, stessa
// logica, stessi service. Cambiano solo import/export (Task 2 del piano "Tab Info del
// drawer partner in stile Notion"). Non ridisegnato in questo task.

// Edit Access Form
export function EditAccessForm({
  access,
  currentProjects,
  onClose,
  onUpdated
}: {
  access: CollaboratorPortalAccess;
  currentProjects: CollaboratorPortalProject[];
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { projects: allProjects, fetchProjects } = useProjectStore();
  const [saving, setSaving] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    currentProjects.map(p => p.project_id)
  );
  const [language, setLanguage] = useState(access.preferred_language || 'it');
  const [permissions, setPermissions] = useState<CollaboratorPortalPermissions>(access.permissions);

  useEffect(() => {
    fetchProjects();
  }, []);

  const activeProjects = (allProjects || []).filter(p => p.status !== 'completed' && p.status !== 'cancelled' && !p.archived);

  const handleSave = async () => {
    setSaving(true);
    try {
      await CollaboratorAuthService.updateCollaboratorAccess(access.id, {
        permissions,
        preferred_language: language,
        project_ids: selectedProjectIds
      });
      onUpdated();
    } catch (error) {
      console.error('Error updating access:', error);
    } finally {
      setSaving(false);
    }
  };

  const permissionLabels: Record<keyof CollaboratorPortalPermissions, string> = {
    view_assigned_tasks: 'Visualizzare task assegnate',
    update_task_status: 'Aggiornare stato task',
    add_comments: 'Aggiungere commenti',
    view_public_comments: 'Visualizzare commenti',
    upload_attachments: 'Caricare allegati',
    log_time: 'Registrare tempo'
  };

  return (
    <div className="border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-black dark:text-white">Modifica accesso: {access.username}</h4>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-white/10 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Language */}
      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">
          Lingua portale
        </label>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
        >
          <option value="it">Italiano</option>
          <option value="en">English</option>
        </select>
      </div>

      {/* Permissions */}
      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-2">
          Permessi
        </label>
        <div className="space-y-2">
          {(Object.keys(permissionLabels) as (keyof CollaboratorPortalPermissions)[]).map((key) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions[key]}
                onChange={(e) => setPermissions(prev => ({ ...prev, [key]: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-black dark:text-white">{permissionLabels[key]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Project Selection */}
      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">
          Progetti accessibili ({selectedProjectIds.length} selezionati)
        </label>
        <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
          {activeProjects.map((project) => (
            <label
              key={project.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedProjectIds.includes(project.id)}
                onChange={(e) => {
                  setSelectedProjectIds(prev =>
                    e.target.checked
                      ? [...prev, project.id]
                      : prev.filter(id => id !== project.id)
                  );
                }}
                className="rounded border-gray-300"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-black dark:text-white truncate block">{project.name}</span>
                {project.client?.business_name && (
                  <span className="text-xs text-gray-500 truncate block">{project.client.business_name}</span>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Annulla
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
        >
          {saving ? <MinimalLoader size="sm" /> : <Check className="w-4 h-4" />}
          Salva modifiche
        </button>
      </div>
    </div>
  );
}
